// Digitale Personalakte (Etappe 6) — Stammdaten, Dokumente und die wiederkehrenden
// Nachweise (Führerscheinkontrolle, Unterweisung, Schulung, Vorsorge) mit Ampel und Erinnerung.
// Darstellung im Widget-Stil (design_handoff_personal §5); die Pille behält ihre Steuerung,
// die Steuerzeile der Seite nutzt dieselben Handler.
//
// Bewusst nicht erfasst: Bankverbindung, Steuer-ID, Sozialversicherungsnummer —
// die gehören in die Lohnbuchhaltung.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { usePillenSteuerung } from '../../../components/island/widgets/SteuerungsAnsicht';
import { CalendarCheck, CircleNotch, PencilSimple, ShieldCheck as ShieldCheckPh, UploadSimple, User, UserCircle, X } from '@phosphor-icons/react';
import { GlasAuswahl } from '../../../components/ui/GlasAuswahl';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { useAppMeldungen } from '../../../components/ui/AppMeldungen';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { PersonalKopf, SteuerKnopf } from './teile/Rahmen';
import {
  DokumenteWidget, NachweisWarnung, NachweiseWidget, StammdatenWidget, UrlaubKacheln,
  type DokumentEntwurf, type NachweisEntwurf,
} from './teile/AkteTeile';
import {
  ladeMitarbeiter, ladeAbwesenheiten, ladeZuordnungen, ladeModelle, ladeVerguetung, verguetungSpeichern,
} from '../../../lib/zeiterfassung/api';
import {
  akteSpeichern, aktuelleNachweise, ampel, dienstjahre, dokumentHochladen, dokumentLoeschen,
  dokumentUrl, ladeAkte, ladeAlleNachweise, ladeDokumente, ladeNachweise, nachweisErfassen,
  nachweisLoeschen, naechsteFaelligkeit,
  type NachweisArt, type Nachweis, type Personalakte, type PersonalDokument,
} from '../../../lib/personal/akte';
import { euroText } from '../../../lib/zeiterfassung/verguetung';
import { urlaubskonto, urlaubsanspruch as berechneUrlaubsanspruch, type Abwesenheit } from '../../../lib/zeiterfassung/abwesenheit';
import { sollMinutenAmTag } from '../../../lib/zeiterfassung/berechnung';
import { darfPersonalVerwalten, gemerkterMitarbeiter, merkeMitarbeiter } from '../../../lib/personal/rechte';

interface Props {
  userProfile?: { id?: string; role?: string } | null;
}

const PersonalakteSeite: React.FC<Props> = ({ userProfile }) => {
  const { notify } = useIslandActions();
  const { mobil } = useMobilModus();
  const { bestaetige, dialoge } = useAppMeldungen(mobil);
  const darfVerwalten = darfPersonalVerwalten(userProfile);
  const meineId = userProfile?.id || '';

  const [mitarbeiter, setMitarbeiter] = useState<{ id: string; full_name: string | null; email: string | null }[]>([]);
  const [gewaehlt, setGewaehlt] = useState(meineId);
  const [akte, setAkte] = useState<Personalakte | null>(null);
  const [dokumente, setDokumente] = useState<PersonalDokument[]>([]);
  const [nachweise, setNachweise] = useState<Nachweis[]>([]);
  const [alleNachweise, setAlleNachweise] = useState<Nachweis[]>([]);
  const [abwesenheiten, setAbwesenheiten] = useState<Abwesenheit[]>([]);
  const [urlaubsanspruch, setUrlaubsanspruch] = useState(30);
  const [sollFn, setSollFn] = useState<(t: string) => number>(() => () => 480);
  const [laedt, setLaedt] = useState(true);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [entwurf, setEntwurf] = useState<Partial<Personalakte>>({});
  const [neuesDokument, setNeuesDokument] = useState<DokumentEntwurf | null>(null);
  const [neuerNachweis, setNeuerNachweis] = useState<NachweisEntwurf | null>(null);
  const [arbeitet, setArbeitet] = useState(false);
  // Stundenlohn liegt bewusst NICHT in der Personalakte, sondern in personal_verguetung:
  // die eigene Akte darf der Mitarbeiter lesen, den Lohn soll er nicht sehen.
  const [stundenlohn, setStundenlohn] = useState<number | null>(null);
  const [lohnEntwurf, setLohnEntwurf] = useState('');

  useEffect(() => {
    if (!darfVerwalten) return;
    void ladeMitarbeiter().then((liste) => {
      setMitarbeiter(liste);
      // Wer selbst nicht zur Belegschaft zählt (Entwickler-/Adminzugang), landet
      // sonst auf einer leeren eigenen Akte — dann den ersten Eintrag zeigen.
      // #548: zuletzt gewählte Person bleibt beim Wechsel Zeiterfassung ↔ Personalakte.
      const gemerkt = gemerkterMitarbeiter();
      if (gemerkt && liste.some((m) => m.id === gemerkt)) setGewaehlt(gemerkt);
      else if (liste.length > 0 && !liste.some((m) => m.id === meineId)) setGewaehlt(liste[0].id);
    });
    void ladeAlleNachweise().then(setAlleNachweise);
  }, [darfVerwalten]);

  const laden = useCallback(async () => {
    if (!gewaehlt) return;
    setLaedt(true);
    const [a, d, n, abw, zu, mod] = await Promise.all([
      ladeAkte(gewaehlt), ladeDokumente(gewaehlt), ladeNachweise(gewaehlt),
      ladeAbwesenheiten(gewaehlt), ladeZuordnungen(gewaehlt), ladeModelle(),
    ]);
    setAkte(a);
    setEntwurf(a || { mitarbeiter_id: gewaehlt });
    setDokumente(d);
    setNachweise(n);
    setAbwesenheiten(abw);
    setUrlaubsanspruch(berechneUrlaubsanspruch(zu, mod));
    if (darfVerwalten) {
      const saetze = await ladeVerguetung();
      const wert = saetze[gewaehlt] ?? null;
      setStundenlohn(wert);
      setLohnEntwurf(wert == null ? '' : String(wert).replace('.', ','));
    }
    setSollFn(() => (t: string) => sollMinutenAmTag(t, zu, mod));
    setLaedt(false);
  }, [gewaehlt]);

  useEffect(() => { void laden(); }, [laden]);

  const konto = useMemo(
    () => urlaubskonto(new Date().getFullYear(), urlaubsanspruch, abwesenheiten, sollFn),
    [abwesenheiten, urlaubsanspruch, sollFn],
  );
  const kranktage = useMemo(() => {
    const jahr = String(new Date().getFullYear());
    return abwesenheiten
      .filter((a) => a.typ === 'krank' && a.status === 'genehmigt' && a.von_tag.startsWith(jahr))
      .reduce((s, a) => {
        const tage = Math.round((new Date(a.bis_tag).getTime() - new Date(a.von_tag).getTime()) / 86400000) + 1;
        return s + tage;
      }, 0);
  }, [abwesenheiten]);

  const offeneNachweise = useMemo(
    () => aktuelleNachweise(alleNachweise).filter((n) => ['faellig', 'bald'].includes(ampel(n.naechste_faellig))),
    [alleNachweise],
  );

  // Dieselben Handler für Pille und Steuerzeile.
  const personWechseln = (w: string) => { setGewaehlt(w); merkeMitarbeiter(w); setBearbeiten(false); };
  const dokumentOeffnen = () => setNeuesDokument({ art: 'vertrag', titel: '', datei: null, gueltigBis: '' });
  const nachweisOeffnen = () => setNeuerNachweis({
    art: 'fuehrerschein', am: new Date().toISOString().slice(0, 10),
    faellig: naechsteFaelligkeit('fuehrerschein', new Date().toISOString().slice(0, 10)), bemerkung: '',
  });

  const stammdatenSpeichern = async () => {
    const roh = lohnEntwurf.trim().replace(',', '.');
    const lohn = roh === '' ? null : Number(roh);
    if (lohn !== null && (!Number.isFinite(lohn) || lohn < 0)) {
      notify({ title: 'Stundenlohn bitte als Zahl angeben, z. B. 14,00', tone: 'error' });
      return;
    }
    setArbeitet(true);
    const fehler = await akteSpeichern({ ...entwurf, mitarbeiter_id: gewaehlt });
    const lohnOk = lohn === stundenlohn ? true : await verguetungSpeichern(gewaehlt, lohn);
    setArbeitet(false);
    if (fehler) { notify({ title: `Speichern fehlgeschlagen: ${fehler}`, tone: 'error' }); return; }
    if (!lohnOk) { notify({ title: 'Stundenlohn konnte nicht gespeichert werden', tone: 'error' }); return; }
    setBearbeiten(false);
    void laden();
  };

  const nachweisEintragen = async () => {
    if (!neuerNachweis) return;
    setArbeitet(true);
    const fehler = await nachweisErfassen({
      mitarbeiterId: gewaehlt, art: neuerNachweis.art, durchgefuehrtAm: neuerNachweis.am,
      naechsteFaellig: neuerNachweis.faellig, bemerkung: neuerNachweis.bemerkung, vonId: meineId,
    });
    setArbeitet(false);
    if (fehler) { notify({ title: fehler, tone: 'error' }); return; }
    setNeuerNachweis(null);
    void laden();
    void ladeAlleNachweise().then(setAlleNachweise);
  };

  const nachweisEntfernen = async (n: Nachweis) => {
    if (!await bestaetige({ nachricht: 'Nachweis löschen?', label: 'Löschen' })) return;
    await nachweisLoeschen(n.id);
    void laden();
  };

  const dokumentAblegen = async () => {
    if (!neuesDokument) return;
    if (!neuesDokument.titel.trim()) { notify({ title: 'Bitte eine Bezeichnung angeben', tone: 'error' }); return; }
    setArbeitet(true);
    const fehler = await dokumentHochladen({
      mitarbeiterId: gewaehlt, art: neuesDokument.art, titel: neuesDokument.titel,
      datei: neuesDokument.datei, gueltigBis: neuesDokument.gueltigBis || null, vonId: meineId,
    });
    setArbeitet(false);
    if (fehler) { notify({ title: fehler, tone: 'error' }); return; }
    setNeuesDokument(null);
    void laden();
  };

  const dokumentEntfernen = async (d: PersonalDokument) => {
    if (!await bestaetige({ nachricht: `„${d.titel}" wirklich löschen?`, label: 'Löschen' })) return;
    await dokumentLoeschen(d);
    void laden();
  };

  const dokumentAnsehen = async (d: PersonalDokument) => {
    if (!d.datei_path) return;
    const url = await dokumentUrl(d.datei_path, d.datei_name);
    if (url) window.open(url, '_blank', 'noopener');
  };

  usePillenSteuerung({
    titel: 'Personalakte',
    eintraege: darfVerwalten
      ? [
          { id: 'mitarbeiter', typ: 'auswahl', farbe: 'violett', icon: UserCircle, kicker: 'Mitarbeiter', wert: gewaehlt, unter: 'Personalakte ansehen',
            optionen: mitarbeiter.map(m => ({ wert: m.id, label: m.full_name || m.email || '' })),
            onChange: personWechseln },
          { id: 'bearbeiten', typ: 'schalter', farbe: 'orange', icon: PencilSimple, kicker: 'Bearbeiten', titel: 'Stammdaten bearbeiten', an: bearbeiten,
            onChange: setBearbeiten, unter: bearbeiten ? 'Bearbeiten beenden' : 'Aus' },
          { id: 'dokument', typ: 'aktion', farbe: 'gold', icon: UploadSimple, kicker: 'Hinzufügen', titel: 'Dokument', unter: 'Dokument hinzufügen',
            onClick: dokumentOeffnen },
          { id: 'nachweis', typ: 'aktion', farbe: 'blau', icon: ShieldCheckPh, kicker: 'Hinzufügen', titel: 'Nachweis', unter: 'Nachweis erfassen',
            onClick: nachweisOeffnen },
        ]
      : [],
  }, [gewaehlt, mitarbeiter, bearbeiten, darfVerwalten]);

  const person = mitarbeiter.find((m) => m.id === gewaehlt);
  const nameVon = (id: string) => mitarbeiter.find((m) => m.id === id)?.full_name || '—';

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <PersonalKopf bereich="personalakte" verwalten={darfVerwalten} mobil={mobil} />

      {/* Steuerzeile — dieselben Handler wie die Pille (nur Personal/GF hat dort Einträge) */}
      {darfVerwalten && (
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <GlasAuswahl wert={gewaehlt} onChange={personWechseln} icon={User} ariaLabel="Mitarbeiter" tip="Mitarbeiter – Akte dieser Person anzeigen"
            style={{ maxWidth: '100%' }} optionen={mitarbeiter.map((m) => ({ wert: m.id, label: m.full_name || m.email || '' }))} />
          <div className="flex flex-wrap" style={{ gap: 8, marginLeft: mobil ? 0 : 'auto' }}>
            <SteuerKnopf icon={ShieldCheckPh} onClick={nachweisOeffnen} tip="Nachweis – Führerscheinkontrolle, Unterweisung, Schulung oder Untersuchung eintragen">Nachweis</SteuerKnopf>
            <SteuerKnopf icon={UploadSimple} onClick={dokumentOeffnen} tip="Dokument – Vertrag, Zeugnis oder Bescheinigung ablegen">Dokument</SteuerKnopf>
            <SteuerKnopf icon={bearbeiten ? X : PencilSimple} primaer={!bearbeiten} onClick={() => setBearbeiten(!bearbeiten)}
              tip={bearbeiten ? 'Bearbeiten beenden – Ohne Speichern zur Ansicht zurück' : 'Stammdaten bearbeiten – Felder dieser Akte ändern'}>
              {bearbeiten ? 'Bearbeiten beenden' : 'Stammdaten bearbeiten'}
            </SteuerKnopf>
          </div>
        </div>
      )}

      {/* Ablaufende Nachweise über alle Mitarbeiter */}
      {darfVerwalten && offeneNachweise.length > 0 && <NachweisWarnung nachweise={offeneNachweise} name={nameVon} onOeffnen={personWechseln} />}

      {laedt ? (
        <div className="flex justify-center" style={{ padding: 48, color: 'var(--ink3)' }}><CircleNotch size={22} className="animate-spin" /></div>
      ) : (
        <>
          <div className="grid items-start" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))' }}>
            <StammdatenWidget name={person?.full_name || person?.email || 'Meine Akte'} akte={akte} jahre={dienstjahre(akte?.eintritt)}
              verwalten={darfVerwalten} bearbeiten={bearbeiten} entwurf={entwurf} onEntwurf={setEntwurf}
              lohnEntwurf={lohnEntwurf} onLohnEntwurf={setLohnEntwurf}
              stundenlohnText={stundenlohn == null ? '' : `${euroText(stundenlohn)} / Std.`}
              arbeitet={arbeitet} onSpeichern={() => { void stammdatenSpeichern(); }} />
            <UrlaubKacheln konto={konto} kranktage={kranktage} />
          </div>

          <div className="grid items-start" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))' }}>
            <NachweiseWidget nachweise={nachweise} verwalten={darfVerwalten} entwurf={neuerNachweis} onEntwurf={setNeuerNachweis} arbeitet={arbeitet}
              onArtWechsel={(art: NachweisArt) => neuerNachweis && setNeuerNachweis({ ...neuerNachweis, art, faellig: naechsteFaelligkeit(art, neuerNachweis.am) })}
              onAmWechsel={(am) => neuerNachweis && setNeuerNachweis({ ...neuerNachweis, am, faellig: naechsteFaelligkeit(neuerNachweis.art, am) })}
              onEintragen={() => { void nachweisEintragen(); }} onSchliessen={() => setNeuerNachweis(null)}
              onLoeschen={(n) => { void nachweisEntfernen(n); }} />
            <DokumenteWidget dokumente={dokumente} verwalten={darfVerwalten} entwurf={neuesDokument} onEntwurf={setNeuesDokument} arbeitet={arbeitet}
              onAblegen={() => { void dokumentAblegen(); }} onSchliessen={() => setNeuesDokument(null)}
              onOeffnen={(d) => { void dokumentAnsehen(d); }} onLoeschen={(d) => { void dokumentEntfernen(d); }} />
          </div>
        </>
      )}

      <p className="m-0 flex items-start" style={{ gap: 8, padding: '0 6px', fontSize: 12, lineHeight: 1.5, color: 'var(--ink3)' }}>
        <CalendarCheck size={14} weight="bold" className="shrink-0" style={{ marginTop: 1 }} />
        Bankverbindung, Steuer-ID und Sozialversicherungsnummer werden hier bewusst nicht geführt —
        die gehören in die Lohnbuchhaltung. Ablaufende Nachweise melden sich 30 Tage vorher von selbst.
      </p>
      {dialoge}
      <GlasTooltipSchicht />
    </div>
  );
};

export default PersonalakteSeite;
